import {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {toast} from 'sonner';
import {getWebSocketBaseUrl} from '@/lib/config';
import {apiSlice} from '@/store/apiSlice';
import {useAppDispatch} from '@/store/hooks';

type ExerciseActiveWsPayload = {
    channel?: string;
    event?: string;
    active_id?: number;
    success?: boolean;
    status?: string;
    detail?: string;
};

/**
 * Subscribe to backend Celery-driven events for one active exercise run (launch, teardown, archive).
 * Invalidates RTK Query caches for the run and console; shows toasts; optionally navigates away when archived.
 */
export function useExerciseActiveRunWebSocket(activeId: string | undefined, options?: { navigateOnArchived?: boolean },) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const navigateOnArchived = options?.navigateOnArchived !== false;

    useEffect(() => {
        if (!activeId || typeof window === 'undefined') return;
        const token = window.localStorage.getItem('access_token');
        if (!token) return;

        const base = getWebSocketBaseUrl();
        if (!base) return;

        const qs = new URLSearchParams({token});
        const url = `${base}/ws/exercise-active/${encodeURIComponent(activeId)}/?${qs.toString()}`;

        let socket: WebSocket;
        try {
            socket = new WebSocket(url);
        } catch {
            return undefined;
        }

        const invalidateRun = () => {
            dispatch(
                apiSlice.util.invalidateTags([
                    {type: 'ExerciseActiveRun', id: activeId},
                    {type: 'ExerciseActiveConsole', id: activeId},
                ]),
            );
        };

        socket.onmessage = (ev) => {
            try {
                const data = JSON.parse(ev.data) as ExerciseActiveWsPayload;
                if (data.channel !== 'exercise_active' || !data.event) return;

                invalidateRun();

                switch (data.event) {
                    case 'launch_completed':
                        toast.success('Infrastructure launch completed. You can start the scenario when ready.');
                        break;
                    case 'launch_failed':
                        toast.error(
                            data.detail?.trim() ||
                                'Infrastructure launch failed. Check scenario configuration and OpenStack capacity.',
                        );
                        break;
                    case 'infrastructure_teardown_finished':
                        if (data.success === false) {
                            toast.warning('OpenStack teardown reported errors; archival may still continue.');
                        } else {
                            toast.success('Cloud resources for this run have been torn down.');
                        }
                        break;
                    case 'scenario_archived':
                        toast.success('Scenario ended and archived.');
                        if (navigateOnArchived) {
                            navigate('/scenarios/actives');
                        }
                        break;
                    default:
                        break;
                }
            } catch {
                /* ignore malformed frames */
            }
        };

        socket.onerror = () => {
            /* non-fatal */
        };

        return () => {
            socket.close();
        };
    }, [activeId, dispatch, navigate, navigateOnArchived]);
}
